import {
  roundUp,
  toNumber,
  formatPercent as formatPercentBase,
  formatCurrencyShort as formatCurrencyShortBase,
  formatCurrencyPlain as formatCurrencyPlainBase,
} from "./formatters";

const formatPercent = (value) => formatPercentBase(value, { normalize: true });

const formatRank = (value) => {
  if (!Number.isFinite(value)) return "-";
  return `${Math.ceil(value)}위`;
};

const formatCurrencyShort = (value, unitOverride = null) => formatCurrencyShortBase(value, unitOverride);

const formatCurrencyPlain = (value, decimals = 2) => formatCurrencyPlainBase(value, decimals);

const parseSeries = (series) => {
  if (!Array.isArray(series)) return [];
  return series
    .map((item, index) => {
      if (item === null || item === undefined) return null;
      if (typeof item === "number") {
        return { date: index + 1, value: item };
      }
      if (Array.isArray(item) && item.length >= 2) {
        return { date: item[0], value: item[1] };
      }
      if (typeof item === "object") {
        const date =
          item.date ??
          item.period;
        const value =
          item.value ??
          item.amount ??
          item.investAmount ??
          item.investamount ??
          item.perf ??
          item.perfguruwhisper ??
          item.y ??
          item.v ??
          item.shares ??
          item.asset;
        return { date: date ?? index + 1, value };
      }
      return null;
    })
    .filter(Boolean)
    .map((point) => {
      const numericValue = typeof point.value === "string" ? parseFloat(point.value) : Number(point.value);
      if (!Number.isFinite(numericValue)) return null;
      return { date: point.date, value: numericValue };
    })
    .filter(Boolean);
};

const parseDateValue = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") {
    return value > 1_000_000_000_000 ? value : null;
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.getTime();
  }
  if (typeof value === "string") {
    const match = value.match(/(\d{4}).*Q([1-4])/i);
    if (match) {
      const year = Number(match[1]);
      const quarter = Number(match[2]);
      if (Number.isFinite(year) && Number.isFinite(quarter)) {
        const month = quarter * 3;
        const parsed = new Date(`${year}-${String(month).padStart(2, "0")}-01`);
        if (!Number.isNaN(parsed.getTime())) return parsed.getTime();
      }
    }
  }
  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) return parsed.getTime();
  return null;
};

const formatLabel = (value, granularity) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "number") return String(value);
  if (typeof value === "string" && /Q[1-4]/i.test(value)) {
    return value.replace(/q/i, "Q");
  }
  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) {
    const year = parsed.getFullYear();
    const month = parsed.getMonth() + 1;
    if (granularity === "quarter") {
      const quarter = Math.floor((month - 1) / 3) + 1;
      return `${year}-Q${quarter}`;
    }
    return `${year}-${String(month).padStart(2, "0")}`;
  }
  if (typeof value === "string") {
    const parts = value.split("-");
    if (parts.length >= 2) {
      const year = parts[0];
      const month = parts[1];
      if (granularity === "quarter") {
        const monthNum = Number(month);
        if (Number.isFinite(monthNum)) {
          const quarter = Math.floor((monthNum - 1) / 3) + 1;
          return `${year}-Q${quarter}`;
        }
      }
      return `${year}-${month}`;
    }
  }
  return String(value);
};

const formatYearMonth = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") {
    if (value.includes("Q")) return value;
    const match = value.match(/(\d{4})[-/.](\d{1,2})/);
    if (match) {
      const month = match[2].padStart(2, "0");
      return `${match[1]}-${month}`;
    }
  }
  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) {
    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
  }
  return String(value);
};

const resolveSeries = (source, keys) => {
  if (Array.isArray(source)) return source;
  if (!source) return null;
  const candidates = Array.isArray(keys) ? keys : [keys];
  for (const key of candidates) {
    if (Array.isArray(source[key])) return source[key];
  }
  if (Array.isArray(source.data)) return source.data;
  return null;
};

const resolveFirstSeries = (sources, keys) => {
  for (const source of sources || []) {
    const series = resolveSeries(source, keys);
    if (series) return series;
  }
  return [];
};

export {
  roundUp,
  toNumber,
  formatPercent,
  formatRank,
  formatCurrencyShort,
  formatCurrencyPlain,
  parseSeries,
  parseDateValue,
  formatLabel,
  formatYearMonth,
  resolveFirstSeries,
};
